import Phaser from 'phaser';

export class ItemSpotlightScene extends Phaser.Scene {
  constructor() {
    super({ key: 'ItemSpotlightScene' });
    this.displayDuration = 60000; // 60 seconds
    this.startTime = null;
  }

  init(data) {
    // Item data can be passed in, or use default
    this.itemData = data?.itemData || this.getDefaultItemData();
  }

  getDefaultItemData() {
    return {
      id: 'sword_of_truth',
      name: 'Sword of Truth',
      rarity: 'Rare',
      type: 'melee',
      damage: { min: 8, max: 20 },
      attackSpeed: 1.2,
      price: 250000,
      priceSymbol: 'SOULWARS',
      // Normal stats for comparison
      normalStats: {
        damage: { min: 8, max: 15 },
        attackSpeed: 1.0
      }
    };
  }

  create() {
    const { width, height } = this.cameras.main;
    
    // Dark background
    this.background = this.add.rectangle(width / 2, height / 2, width, height, 0x0F172A);
    
    // Create UI
    this.createItemDisplay(width, height);
    this.createCountdownTimer(width, height);
    
    // Start timer
    this.startTime = this.time.now;
    
    // Auto-transition after duration
    this.time.delayedCall(this.displayDuration, () => {
      this.scene.start('JoinScene');
    });
  }

  createItemDisplay(width, height) {
    const container = this.add.container(width / 2, height / 2);
    
    // Card dimensions
    const cardWidth = 1200;
    const cardHeight = 620;
    const cardX = 0;
    const cardY = 0;
    
    // Create card background with border
    const cardBg = this.add.rectangle(cardX, cardY, cardWidth, cardHeight, 0x1E293B);
    cardBg.setStrokeStyle(4, 0x475569);
    container.add(cardBg);
    
    // Add subtle glow effect for rare items
    const rarityColor = this.getRarityColor(this.itemData.rarity);
    const glowColor = Phaser.Display.Color.HexStringToColor(rarityColor).color;
    const glow = this.add.rectangle(cardX, cardY, cardWidth + 8, cardHeight + 8, glowColor, 0.2);
    glow.setBlendMode(Phaser.BlendModes.ADD);
    container.addAt(glow, 0);
    
    // Left side: Item Image
    const imageX = cardX - cardWidth / 2 + 310;
    const imageY = cardY;
    const imageSize = 600;
    
    // Try to load item image, fallback to placeholder
    let itemImage = null;
    const imageKey = this.itemData.imageKey || `item_${this.itemData.id}`;
    
    if (this.textures.exists(imageKey)) {
      itemImage = this.add.image(imageX, imageY, imageKey);
      itemImage.setDisplaySize(imageSize, imageSize);
      itemImage.setTint(Phaser.Display.Color.HexStringToColor(rarityColor).color);
    } else {
      // Create placeholder - a stylized sword icon
      const placeholder = this.add.graphics();
      placeholder.fillStyle(Phaser.Display.Color.HexStringToColor(rarityColor).color, 0.8);
      placeholder.fillRect(imageX - imageSize / 2, imageY - imageSize / 2, imageSize, imageSize);
      
      // Draw a simple sword shape
      placeholder.lineStyle(8, 0xFFFFFF, 1);
      // Blade
      placeholder.beginPath();
      placeholder.moveTo(imageX, imageY - imageSize / 2 + 50);
      placeholder.lineTo(imageX, imageY + imageSize / 2 - 50);
      placeholder.strokePath();
      // Cross guard
      placeholder.lineStyle(6, 0xFFFFFF, 1);
      placeholder.beginPath();
      placeholder.moveTo(imageX - 60, imageY - 20);
      placeholder.lineTo(imageX + 60, imageY - 20);
      placeholder.strokePath();
      // Handle
      placeholder.lineStyle(8, 0x8B4513, 1);
      placeholder.beginPath();
      placeholder.moveTo(imageX, imageY + imageSize / 2 - 50);
      placeholder.lineTo(imageX, imageY + imageSize / 2 - 20);
      placeholder.strokePath();
      
      container.add(placeholder);
    }
    
    if (itemImage) {
      container.add(itemImage);
    }
    
    // Right side: Item Info - starts 20px to the right of the item image
    const imageRightEdge = imageX + imageSize / 2;
    const infoX = imageRightEdge + 20;
    const infoStartY = cardY - cardHeight / 2 + 50;
    
    // Item Name
    const itemName = this.add.text(infoX, infoStartY, this.itemData.name, {
      fontSize: '56px',
      color: rarityColor,
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 5
    }).setOrigin(0, 0.5);
    container.add(itemName);
    
    // Rarity Badge
    const rarityY = infoStartY + 60;
    const rarityText = this.add.text(infoX, rarityY, this.itemData.rarity.toUpperCase(), {
      fontSize: '24px',
      color: '#FFFFFF',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold',
      backgroundColor: rarityColor,
      padding: { x: 15, y: 8 },
      stroke: '#000000',
      strokeThickness: 2
    }).setOrigin(0, 0.5);
    container.add(rarityText);
    
    // Item Type
    const typeY = rarityY + 45;
    const typeText = this.add.text(infoX, typeY, `${this.itemData.type.charAt(0).toUpperCase() + this.itemData.type.slice(1)} Weapon`, {
      fontSize: '28px',
      color: '#9CA3AF',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2
    }).setOrigin(0, 0.5);
    container.add(typeText);
    
    // Stats Section Title
    const statsTitleY = typeY + 70;
    const statsTitle = this.add.text(infoX, statsTitleY, 'Stats', {
      fontSize: '36px',
      color: '#FFFFFF',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 3
    }).setOrigin(0, 0.5);
    container.add(statsTitle);
    
    // Damage Stat
    const damageY = statsTitleY + 60;
    const normalDamage = `${this.itemData.normalStats.damage.min}-${this.itemData.normalStats.damage.max}`;
    const specialDamage = `${this.itemData.damage.min}-${this.itemData.damage.max}`;
    const damageImproved = this.itemData.damage.max > this.itemData.normalStats.damage.max;
    
    const damageLabel = this.add.text(infoX, damageY, 'Damage:', {
      fontSize: '32px',
      color: '#FFFFFF',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 3
    }).setOrigin(0, 0.5);
    container.add(damageLabel);

    // Calculate damage value position to avoid overlap (label width + spacing)
    const damageLabelWidth = damageLabel.width;
    const damageValueX = infoX + damageLabelWidth + 20;
    const damageValue = this.add.text(damageValueX, damageY, specialDamage, {
      fontSize: '32px',
      color: damageImproved ? '#10B981' : '#FFFFFF',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 3
    }).setOrigin(0, 0.5);
    container.add(damageValue);

    // Show comparison if improved
    if (damageImproved) {
      const damageValueWidth = damageValue.width;
      const comparisonText = this.add.text(damageValueX + damageValueWidth + 15, damageY, `(+${this.itemData.damage.max - this.itemData.normalStats.damage.max} vs ${normalDamage})`, {
        fontSize: '22px',
        color: '#10B981',
        fontFamily: 'Arial, sans-serif',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 2
      }).setOrigin(0, 0.5);
      container.add(comparisonText);
    }

    // Attack Speed Stat
    const speedY = damageY + 50;
    const normalSpeed = this.itemData.normalStats.attackSpeed.toFixed(1);
    const specialSpeed = this.itemData.attackSpeed.toFixed(1);
    const speedImproved = this.itemData.attackSpeed > this.itemData.normalStats.attackSpeed;
    
    const speedLabel = this.add.text(infoX, speedY, 'Attack Speed:', {
      fontSize: '32px',
      color: '#FFFFFF',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 3
    }).setOrigin(0, 0.5);
    container.add(speedLabel);

    // Calculate speed value position to avoid overlap (label width + spacing)
    const speedLabelWidth = speedLabel.width;
    const speedValueX = infoX + speedLabelWidth + 20;
    const speedValue = this.add.text(speedValueX, speedY, specialSpeed, {
      fontSize: '32px',
      color: speedImproved ? '#10B981' : '#FFFFFF',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 3
    }).setOrigin(0, 0.5);
    container.add(speedValue);

    // Show comparison if improved
    if (speedImproved) {
      const speedDiff = (this.itemData.attackSpeed - this.itemData.normalStats.attackSpeed).toFixed(1);
      const speedValueWidth = speedValue.width;
      const comparisonText = this.add.text(speedValueX + speedValueWidth + 15, speedY, `(+${speedDiff} vs ${normalSpeed})`, {
        fontSize: '22px',
        color: '#10B981',
        fontFamily: 'Arial, sans-serif',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 2
      }).setOrigin(0, 0.5);
      container.add(comparisonText);
    }

    // Quantity Available
    const quantityY = speedY + 80;
    const quantityText = this.add.text(infoX, quantityY, 'Quantity Available: 1', {
      fontSize: '32px',
      color: '#F3F300',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 3
    }).setOrigin(0, 0.5);
    container.add(quantityText);

    // Price Section
    const priceY = quantityY + 60;
    const priceText = this.add.text(infoX, priceY, `${this.itemData.price.toLocaleString()} ${this.itemData.priceSymbol}`, {
      fontSize: '48px',
      color: '#F3F300',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 4
    }).setOrigin(0, 0.5);
    container.add(priceText);

    // Purchase Instruction
    const purchaseY = priceY + 75;
    const purchaseText = this.add.text(infoX, purchaseY, 'Type "buy" in chat to purchase', {
      fontSize: '36px',
      color: '#10B981',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 3
    }).setOrigin(0, 0.5);
    container.add(purchaseText);
  }

  createCountdownTimer(width, height) {
    // Countdown timer at bottom (outside container, directly on scene)
    const countdownText = this.add.text(width / 2, height - 80, 'Time left to purchase: 10s', {
      fontSize: '32px',
      color: '#F3F300',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 4
    }).setOrigin(0.5);
    this.countdownText = countdownText;
  }

  getRarityColor(rarity) {
    const colors = {
      'Common': '#9CA3AF',
      'Uncommon': '#10B981',
      'Rare': '#3B82F6',
      'Epic': '#8B5CF6',
      'Legendary': '#F59E0B'
    };
    return colors[rarity] || '#FFFFFF';
  }

  update(time, delta) {
    if (!this.startTime) return;
    
    const elapsed = time - this.startTime;
    const remaining = Math.max(0, this.displayDuration - elapsed);
    const remainingSeconds = Math.ceil(remaining / 1000);
    
    if (this.countdownText) {
      this.countdownText.setText(`Time left to purchase: 1${remainingSeconds}s`);
    }
  }
}

